import { Loader } from "@/components/common/loader";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

export type KeyValuePairProps = {
  label: string | React.ReactNode;
  value?: string | React.ReactNode;
  className?: string;
  labelClassName?: string;
  valueClassName?: string;
  testId?: string;
};

const KeyValuePair = ({
  label,
  value,
  className,
  labelClassName,
  valueClassName,
  testId,
}: KeyValuePairProps) => {
  return (
    <div
      className={cn(
        "flex items-center justify-between text-base text-brand-green1-300",
        className,
      )}
      data-pw={testId}
    >
      <span className={labelClassName}>{label}</span>
      <span className={valueClassName} data-pw={`${testId}-value`}>
        {value ?? "-"}
      </span>
    </div>
  );
};

export const PriceElement = ({
  header,
  preContent,
  content,
  discount,
  price,
  footer,
  loading,
  customContent,
  customContentClassName,
}: {
  header: {
    icon?: React.ReactNode;
    label?: string;
  };
  // Rendered on the card background between the header and the grey content
  // section (e.g. the collapsible "Produkty" row on checkout).
  preContent?: React.ReactNode;
  content: {
    header?: React.ReactNode;
    kvs?: KeyValuePairProps[];
    footer?: React.ReactNode;
  };
  discount?: {
    header?: React.ReactNode;
    kvs?: KeyValuePairProps[];
  };
  price?: {
    header?: React.ReactNode;
    kvs?: KeyValuePairProps[];
  };
  customContent?: {
    header?: React.ReactNode;
    kvs?: KeyValuePairProps[];
    wrapperClassName?: string;
    footer?: React.ReactNode;
  }[];
  customContentClassName?: string;
  footer: {
    header?: React.ReactNode;
    kvs?: KeyValuePairProps[];
    buttons?: React.ReactNode;
    other?: React.ReactNode;
    className?: string;
  };

  loading?: boolean;
}) => {
  return (
    <Card className="order-2 border bg-background p-0 md:sticky md:order-1">
      <CardHeader className="p-4">
        <div className="flex items-center gap-2 font-medium text-xl">
          {header.icon}
          {header.label}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {preContent}
        <div className="space-y-2 bg-brand-grey1 p-4">
          {content.header}
          <div className="flex flex-col gap-1">
            {content.kvs?.map((kv) => (
              <KeyValuePair {...kv} key={`${kv.label}-${kv.value}`} />
            ))}
          </div>
          {content.footer}
        </div>

        {discount?.kvs && discount.kvs.length > 0 && (
          <div className="p-4 pb-0">
            <div className="flex flex-col gap-1 border-b pb-4">
              {discount.header && (
                <header className="font-medium">{discount.header}</header>
              )}
              {discount.kvs?.map((kv) => (
                <KeyValuePair {...kv} key={`${kv.label}-${kv.value}`} />
              ))}
            </div>
          </div>
        )}

        {price && (
          <div className="p-4 pb-0">
            <div className="flex flex-col gap-1 border-b pb-4">
              {price.header && (
                <header className="font-medium">{price.header}</header>
              )}
              {price.kvs?.map((kv) => (
                <KeyValuePair {...kv} key={`${kv.label}-${kv.value}`} />
              ))}
            </div>
          </div>
        )}
      </CardContent>
      {customContent && (
        <CardContent className={cn("p-4", customContentClassName)}>
          <PriceElementCustomContent elements={customContent} />
        </CardContent>
      )}
      <CardFooter className={cn("block w-full pb-4", footer.className)}>
        {footer.header}
        <div>
          <div className="flex flex-col gap-1">
            {footer.kvs?.map((kv) => (
              <KeyValuePair {...kv} key={`${kv.label}-${kv.value}`} />
            ))}
          </div>
          {footer.other && <div className="w-full">{footer.other}</div>}
          {footer.buttons}
        </div>
      </CardFooter>
      {loading && (
        <div className="absolute top-0 left-0 grid h-full w-full place-items-center rounded-lg bg-brand-green1-300/10">
          <Loader />
        </div>
      )}
    </Card>
  );
};

const PriceElementCustomContent = ({
  elements,
}: {
  elements: {
    header?: React.ReactNode;
    kvs?: KeyValuePairProps[];
    wrapperClassName?: string;
    footer?: React.ReactNode;
  }[];
}) => {
  return (
    <>
      {elements.map((element, index) => (
        <div className={cn(element.wrapperClassName)} key={index}>
          {element.header}
          {element.kvs?.map((kv) => (
            <KeyValuePair {...kv} key={`${kv.label}-${kv.value}`} />
          ))}
          {element.footer}
        </div>
      ))}
    </>
  );
};
